import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { LogoUpload } from '../components/LogoUpload';
import { useSession, signOut } from '../lib/auth';
import { useUserOrg } from '../lib/org';
import { updateOrgName } from '../lib/db';
import { BRAND } from '../config/brand';
import { LoadingIndicator } from '../components/LoadingIndicator';

export function Settings() {
  const { session } = useSession();
  const { org, refresh } = useUserOrg();
  const canEditOrgName = !!org && org.role === 'owner';

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  function startEditName() {
    setNameDraft(org?.org_name ?? '');
    setNameError(null);
    setEditingName(true);
  }

  function cancelEditName() {
    setEditingName(false);
    setNameError(null);
  }

  async function saveOrgName(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    setSavingName(true);
    setNameError(null);
    try {
      await updateOrgName(org.org_id, nameDraft);
      await refresh();
      setEditingName(false);
    } catch (err) {
      setNameError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingName(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    window.location.href = '/login';
  }

  return (
    <AppShell title="Settings" back="/">
      <div className="card">
        <h3 style={{ margin: '0 0 8px' }}>Account</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Signed in as <strong>{session?.user.email}</strong>
        </p>
        {org && !editingName && (
          <div style={{ margin: '8px 0 0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
              Company: <strong>{org.org_name}</strong> · {org.role}
            </p>
            {canEditOrgName && (
              <button
                type="button"
                className="btn-link-sm"
                onClick={startEditName}
                aria-label="Change company name"
              >
                ✎ Change
              </button>
            )}
          </div>
        )}

        {org && editingName && (
          <form onSubmit={saveOrgName} style={{ marginTop: 10 }}>
            <div className="field">
              <label htmlFor="org-name-edit">Company name</label>
              <input
                id="org-name-edit"
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                autoFocus
                autoComplete="organization"
                enterKeyHint="done"
                maxLength={120}
                required
                disabled={savingName}
              />
              <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
                Appears at the top of every PDF you generate, on every invite email, and on every site sign-in.
              </p>
            </div>
            {nameError && <div className="banner banner-error" style={{ marginTop: 4 }}>{nameError}</div>}
            <div className="btn-stack" style={{ marginTop: 8 }}>
              <button type="submit" className="btn btn-primary" disabled={savingName || !nameDraft.trim()}>
                {savingName ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={cancelEditName} disabled={savingName}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      <Link to="/welcome" className="card tile-link">
        <h3 style={{ margin: '0 0 4px' }}>Setup wizard →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>Logo, H&amp;S Policy, first site — re-run any time.</p>
      </Link>

      <Link to="/sites" className="card tile-link">
        <h3 style={{ margin: '0 0 4px' }}>Sites →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>Add, rename or archive sites. Set the geofence radius for sign-in checks here too.</p>
      </Link>

      <div className="card">
        <h3 style={{ margin: '0 0 4px' }}>Customer logo</h3>
        <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
          Appears at the top of every PDF you generate — sign-ins, RAMS, accidents, permits, CPP (Construction Phase Plan). Recommended size: square, at least 256×256.
        </p>
        {org ? (
          <LogoUpload
            orgId={org.org_id}
            currentLogoPath={org.logo_path}
            onChange={refresh}
          />
        ) : (
          <LoadingIndicator />
        )}
      </div>

      <Link to="/settings/features" className="card tile-link">
        <h3 style={{ margin: '0 0 4px' }}>Features →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>Turn parts of the app on or off — CPP (Construction Phase Plan), F10 (HSE notifiable-project form), permits, plant register and more.</p>
      </Link>

      {/* Affiliate-programme settings tile hidden until Tapfiliate is wired
          (M17 on the launch tracker). Showing "coming soon" copy on a paid
          product's settings looks unfinished, and most app users aren't
          affiliates anyway — affiliate sign-up lives on the public
          marketing site at safeandsorted.com/affiliate.html. Re-enable
          when commission payouts go live. */}

      <Link to="/settings/team" className="card tile-link">
        <h3 style={{ margin: '0 0 4px' }}>Add users →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Invite the rest of your team — anyone who needs to use the app on their phone.
        </p>
      </Link>

      <Link to="/settings/plan" className="card tile-link">
        <h3 style={{ margin: '0 0 4px' }}>Plan &amp; billing →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Which tier you're on, what's coming up, who to email for an invoice.
        </p>
      </Link>

      <Link to="/settings/security" className="card tile-link">
        <h3 style={{ margin: '0 0 4px' }}>Sign-in security →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          See every phone and computer signed in. Sign out devices you don't recognise.
        </p>
      </Link>

      <Link to="/terms" className="card tile-link">
        <h3 style={{ margin: '0 0 4px' }}>Terms & Conditions →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          App terms and the disclaimer that appears on every RAMS PDF.
        </p>
      </Link>

      <Link to="/privacy" className="card tile-link">
        <h3 style={{ margin: '0 0 4px' }}>Privacy policy →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          What we collect, why, where it's stored and your rights under UK GDPR.
        </p>
      </Link>

      {/*
        Delete account link — required by Apple Guideline 5.1.1(v). Every app
        that supports account creation must expose a delete-account entry
        point inside the app. Opens the public delete-account page which
        walks the user through the DSR request. External link (safe: same
        origin as our marketing site) so we can host the form/confirm flow
        once and use it from web + iOS + Android.
      */}
      <a
        href="https://safeandsorted.com/delete-account.html"
        target="_blank"
        rel="noopener"
        className="card tile-link"
        style={{ borderColor: 'var(--danger-soft, #FCA5A5)' }}
      >
        <h3 style={{ margin: '0 0 4px', color: 'var(--danger)' }}>Delete my account →</h3>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Permanently delete your Safe and Sorted account and all associated
          data. Opens our account-deletion page.
        </p>
      </a>

      <button
        type="button"
        className="btn btn-primary"
        style={{ marginTop: 24, background: 'var(--danger)', borderColor: 'var(--danger)', color: '#FFF' }}
        onClick={handleSignOut}
      >
        Sign out
      </button>

      <p className="muted" style={{ fontSize: 11, textAlign: 'center', margin: '24px 0 8px', lineHeight: 1.5 }}>
        {BRAND.name} · operated by {BRAND.legalEntity} (company no. {BRAND.companyNumber})<br />
        Registered in {BRAND.companyJurisdiction} · Registered office: {BRAND.registeredOffice}
      </p>
    </AppShell>
  );
}
